import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaTimes, FaChevronDown } from "react-icons/fa";
import { ChevronDown } from "lucide-react";
import { fetchCompanyProfile } from "../../../Redux/companySlice";
import UserProfileMenu from "../../../components/UserProfileMenu";
import {
  MdDashboard,
  MdGroup,
  MdAssessment,
  MdAttachMoney,
  MdSettings,
  MdCampaign,
  MdPeople,
  MdLink,
} from "react-icons/md";

const EXPANDED_WIDTH = 250;
const COLLAPSED_WIDTH = 72;

const CompanyDashboard = ({
  isOpen,
  setIsOpen,
  isCollapsed,
  setIsCollapsed,
  iconOnly,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { company, isAuthenticated, loading } = useSelector((state) => state.company);
  const [openMenu, setOpenMenu] = useState(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && !company) {
      dispatch(fetchCompanyProfile());
    }
  }, [dispatch, company, isAuthenticated, loading]);

  const isCampaignSectionActive =
    location.pathname.includes("/campaigns") ||
    location.pathname.includes("/pipelines");

  useEffect(() => {
    if (isCampaignSectionActive) {
      setOpenMenu("campaigns");
    }
  }, [isCampaignSectionActive]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, setIsOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setIsOpen]);

  const companyData = company?.company || company;
  const normalizedAccountType = String(
    companyData?.companyAccountType || "agencies"
  ).toLowerCase();
  const isCompanyAccountType =
    normalizedAccountType === "brand" || normalizedAccountType === "brands";

  const isPathActive = (path) => location.pathname.includes(path);

  const getLinkClasses = (isActive) =>
    `relative flex items-center cursor-pointer rounded-lg transition-all duration-200 ${
      iconOnly ? "justify-center px-2 py-2.5" : "justify-between px-3 py-2.5"
    } ${
      isActive
        ? "bg-orange-100 text-orange-600 font-semibold"
        : "text-gray-700 hover:bg-orange-50 hover:text-orange-600"
    }`;

  const getSubItemClasses = (path) => {
    const isActive = isPathActive(path);
    return `flex items-center py-2 px-4 text-sm cursor-pointer rounded-lg transition-all duration-200 ${
      isActive
        ? "bg-orange-50 text-orange-600 font-semibold shadow-sm border-l-2 border-orange-400"
        : "text-gray-600 hover:bg-orange-50 hover:text-orange-600 hover:shadow-sm"
    }`;
  };

  const sidebarWidth = iconOnly ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-x-0 top-[70px] bottom-0 bg-black bg-opacity-50 z-[125] md:hidden" />
      )}

      <div
        ref={sidebarRef}
        className={`
          fixed top-[70px] left-0 bottom-0 bg-white shadow-xl
          transform transition-all duration-300 ease-in-out
          md:translate-x-0 md:shadow-lg md:border-r md:border-gray-200 md:z-40
          ${isOpen ? "translate-x-0 z-[130]" : "-translate-x-full z-40"}
        `}
        style={{ width: sidebarWidth }}
      >
        <div className="relative w-full h-full min-h-0 bg-white flex flex-col overflow-visible">
          <button
            type="button"
            onClick={() => {
              setIsCollapsed((prev) => {
                if (!prev) setOpenMenu(null);
                return !prev;
              });
            }}
            className="absolute top-4 -right-3 z-50 hidden h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800 md:flex"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <ChevronDown
              className={`h-3.5 w-3.5 ${isCollapsed ? "-rotate-90" : "rotate-90"}`}
            />
          </button>

          <div className="flex-shrink-0 flex justify-end px-4 pt-3 md:hidden">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close sidebar"
            >
              <FaTimes className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          <nav
            className={`flex-1 min-h-0 overflow-y-auto pb-6 space-y-1 ${
              iconOnly ? "px-2 pt-8" : "p-3 pt-8 md:pt-10"
            }`}
          >
            <Link
              to="/company/dashboard/overview"
              className={getLinkClasses(isPathActive("/overview"))}
              title={iconOnly ? "Dashboard" : undefined}
            >
              <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                <MdDashboard className="w-5 h-5 shrink-0" />
                {!iconOnly ? <span className="font-medium truncate">Dashboard</span> : null}
              </div>
            </Link>

            <Link
              to="/company/dashboard/brands"
              className={getLinkClasses(isPathActive("/brands"))}
              title={iconOnly ? "Brands" : undefined}
            >
              <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                <MdGroup className="w-5 h-5 shrink-0" />
                {!iconOnly ? <span className="font-medium truncate">Brands</span> : null}
              </div>
            </Link>

            <div className="relative mb-1">
              <div
                onClick={() => {
                  if (iconOnly) {
                    setIsCollapsed(false);
                    setOpenMenu("campaigns");
                    return;
                  }
                  setOpenMenu(openMenu === "campaigns" ? null : "campaigns");
                }}
                className={getLinkClasses(isCampaignSectionActive)}
                title={iconOnly ? "Campaigns" : undefined}
              >
                <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                  <MdCampaign className="w-5 h-5 shrink-0" />
                  {!iconOnly ? (
                    <span className="font-medium truncate">Campaigns</span>
                  ) : null}
                </div>
                {!iconOnly ? (
                  <FaChevronDown
                    className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${
                      openMenu === "campaigns" ? "rotate-180" : ""
                    }`}
                  />
                ) : null}
              </div>
              {openMenu === "campaigns" && !iconOnly && (
                <div className="ml-8 mt-2 space-y-1">
                  <Link
                    to="/company/dashboard/campaigns"
                    className={getSubItemClasses("/campaigns")}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/company/dashboard/pipelines"
                    className={getSubItemClasses("/pipelines")}
                  >
                    Pipelines
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/company/dashboard/affiliates"
              className={getLinkClasses(isPathActive("/affiliates"))}
              title={iconOnly ? "Affiliates" : undefined}
            >
              <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                <MdPeople className="w-5 h-5 shrink-0" />
                {!iconOnly ? <span className="font-medium truncate">Affiliates</span> : null}
              </div>
            </Link>

            <Link
              to="/company/dashboard/financial"
              className={getLinkClasses(isPathActive("/financial"))}
              title={iconOnly ? "Financial" : undefined}
            >
              <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                <MdAttachMoney className="w-5 h-5 shrink-0" />
                {!iconOnly ? <span className="font-medium truncate">Financial</span> : null}
              </div>
            </Link>

            <Link
              to="/company/dashboard/social-accounts"
              className={getLinkClasses(isPathActive("/social-accounts"))}
              title={iconOnly ? "Connect Social Accounts" : undefined}
            >
              <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                <MdLink className="w-5 h-5 shrink-0" />
                {!iconOnly ? (
                  <span className="font-medium truncate">Connect Social Accounts</span>
                ) : null}
              </div>
            </Link>

            {isCompanyAccountType && (
              <Link
                to="/company/dashboard/dm-automation"
                className={getLinkClasses(isPathActive("/dm-automation"))}
                title={iconOnly ? "DM Automation" : undefined}
              >
                <div className={`flex items-center min-w-0 ${iconOnly ? "" : "space-x-3"}`}>
                  <MdCampaign className="w-5 h-5 shrink-0" />
                  {!iconOnly ? (
                    <span className="font-medium truncate">DM Automation</span>
                  ) : null}
                </div>
              </Link>
            )}

            <div
              className={`flex items-center rounded-lg text-gray-400 cursor-not-allowed bg-gray-50 ${
                iconOnly ? "justify-center px-2 py-2.5" : "space-x-3 px-3 py-2.5"
              }`}
              title="Coming soon"
            >
              <MdAssessment className="w-5 h-5 shrink-0" />
              {!iconOnly ? <span className="font-medium truncate">Reports (Coming Soon)</span> : null}
            </div>

            <div
              className={`flex items-center rounded-lg text-gray-400 cursor-not-allowed bg-gray-50 ${
                iconOnly ? "justify-center px-2 py-2.5" : "space-x-3 px-3 py-2.5"
              }`}
              title="Coming soon"
            >
              <MdSettings className="w-5 h-5 shrink-0" />
              {!iconOnly ? <span className="font-medium truncate">Settings (Coming Soon)</span> : null}
            </div>
          </nav>

          <div className={`flex-shrink-0 border-t border-gray-200 ${iconOnly ? "p-2" : "p-3"}`}>
            <UserProfileMenu
              variant="sidebar"
              className="w-full"
              showDashboardAction={false}
              collapsed={iconOnly}
              closeMobileSidebar={() => setIsOpen(false)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyDashboard;
